import { ReactNode, useState } from 'react';
import DocumentPreviewDialog from './index';
import { downloadFile, filenameFromUrl } from './downloadFile';

interface PreviewTarget {
  url: string;
  filename: string;
}

/**
 * In-app preview for any stored file link (ZLAYT-1445): call openPreview
 * where the code used to window.open() the storage URL, and render
 * previewDialog once in the component.
 */
export function useDocumentPreview(): {
  openPreview: (url?: string | null, filename?: string | null) => void;
  previewDialog: ReactNode;
} {
  const [target, setTarget] = useState<PreviewTarget | null>(null);

  const openPreview = (url?: string | null, filename?: string | null) => {
    if (!url) return;
    setTarget({ url, filename: filename || filenameFromUrl(url) });
  };

  // The dialog is portalled, but React still bubbles its events up the
  // component tree; stop them here so a click in the preview never reaches a
  // table row or tag handler that owns the link.
  const previewDialog = (
    <span
      style={{ display: 'contents' }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="presentation"
    >
      <DocumentPreviewDialog
        open={Boolean(target)}
        onClose={() => setTarget(null)}
        url={target?.url}
        filename={target?.filename ?? ''}
        onDownload={
          target ? () => downloadFile(target.url, target.filename) : undefined
        }
      />
    </span>
  );

  return { openPreview, previewDialog };
}
